import React, { useEffect, useMemo, useState } from "react";
import "./Home.css";
import SplitPanel from "./common/SplitPanel";
import ContactCTA from "./common/ContactCTA";
import ContactButton from "./common/ContactButton";

import heroBg        from "./../assets/home/hero.webp";
import aboutImg      from "./../assets/home/abCover.webp";
import pergolaPanel  from "./../assets/pergolas/pergolaPanel.webp";
import partitionPanel from "./../assets/partitions/partitionPanel.webp";
import doorPanel     from "./../assets/doors/doorPanel.webp";
import sliderPanel   from "./../assets/doors/slidingPanel.webp";

// Detect mobile/touch synchronously so we never mount heavy media on mobile
function detectMobile() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(pointer: coarse)").matches ||
    window.matchMedia("(max-width: 768px)").matches
  );
}

const Home = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [, setIsMobile] = useState(detectMobile);

  const collectionPanels = useMemo(() => [
    { title: "Pergola Systems",    cta: "Discover", path: "/collections/pergolas",    image: pergolaPanel },
    { title: "Partition Systems",  cta: "Discover", path: "/collections/partitions",  image: partitionPanel },
    { title: "Door Systems",       cta: "Discover", path: "/collections/doors",       image: doorPanel },
    { title: "Sliding Systems",    cta: "Discover", path: "/collections/sliders",     image: sliderPanel },
  ], []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => setHeroVisible(true));
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const touch = window.matchMedia("(pointer: coarse)");
    const narrow = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(touch.matches || narrow.matches);
    touch.addEventListener("change", update);
    narrow.addEventListener("change", update);
    return () => {
      touch.removeEventListener("change", update);
      narrow.removeEventListener("change", update);
    };
  }, []);

  return (
    <div>
      <main>
        {/* ─── Hero ─── */}
        <section className={`hero hero--animated${heroVisible ? " hero--visible" : ""}`}>
          <img
            className="hero-bg-img"
            src={heroBg}
            alt="Astra Concepts aluminum installation"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="hero-content">
            <h1 className="hero-headline">ASTRA CONCEPTS</h1>
            <p className="hero-tagline">Aluminum Design Systems</p>
          </div>
        </section>


        {/* ─── About ─── */}
        <SplitPanel
          ariaLabel="About Astra Concepts"
          media={aboutImg}
          depth={0.08}
          bodyClassName="about-content about-grid"
          overlay
          left={
            <div className="about-left">
              <div className="about-large">About</div>
              <div className="about-year">Premium Aluminum</div>
            </div>
          }
          right={
            <div className="about-right">
              <div className="about-kicker">LEARN</div>
              <h2 className="about-title">Astra Concepts</h2>
              <p className="about-desc">
                At Astra Concepts, luxury and functionality merge in every installation. We specialize in high-end aluminum systems — from bioclimatic pergolas to sophisticated partition and door systems — designed to transform your spaces into modern, enduring masterpieces.
                <br /><br />
                Driven by precision, innovation, and a shared commitment to quality with our sister company Varsa Windows &amp; Doors, we bring decades of craftsmanship to every project.
              </p>
              <div className="about-actions">
                <ContactButton to="/about" className="about-cta">
                  Learn More
                </ContactButton>
              </div>
            </div>
          }
        />

        {/* ─── Collections ─── */}
        {collectionPanels.map((panel) => (
          <SplitPanel
            key={panel.title}
            ariaLabel={panel.title}
            className="collection-panel"
            media={panel.image}
            depth={0.1}
            overlay
            bodyClassName="about-content about-grid2"
            reverse={false}
            left={
              <div className="about-left">
                <div className="about-large">{panel.title}</div>
              </div>
            }
            right={
              <div className="about-left">
                <div className="about-actions">
                  <ContactButton to={panel.path} className="about-cta">
                    {panel.cta}
                  </ContactButton>
                </div>
              </div>
            }
          />
        ))}

        {/* ─── Contact CTA ─── */}
        <ContactCTA
          kicker="Engage"
          title="Let's Elevate Your Next Space"
          description="From custom pergolas to large-scale commercial partition systems, Astra Concepts delivers premium aluminum systems with the engineering, detailing, and installation support to match."
          primaryAction={{ label: "Book a Consultation", to: "/contact" }}
        />
      </main>
    </div>
  );
};

export default Home;
